<template>
  <div class="rating-screen" data-testid="screen-rating">
    <template v-if="confirmed">
      <BranchLogo :branch="branch" class="brand" />
      <h1 class="title" data-testid="rating-confirmed">ได้รับคะแนนแล้ว ขอบคุณค่ะ</h1>
    </template>
    <template v-else>
      <BranchLogo :branch="branch" class="brand" />
      <!-- Immediate, non-final feedback the instant a rating is chosen (tap
           or the Phase 6 helper bridge alike - both go through the same
           onSelect/submitFromHelper below) - never the final "thank you"
           wording before the backend actually confirms. -->
      <h1 v-if="ratingSubmitting" class="title" data-testid="rating-submitting">กำลังส่งคะแนน...</h1>
      <h1 v-else class="title">กรุณาประเมินการบริการ</h1>
      <RatingChoices size="full" :selected="selected" :disabled="ratingSubmitting" @select="onSelect" />
      <p v-if="errorMessage" class="retry-message" data-testid="rating-retry-message">{{ errorMessage }}</p>
    </template>
  </div>
</template>

<script>
import BranchLogo from './BranchLogo.vue';
import RatingChoices from './RatingChoices.vue';
import { scheduleAfter, watchUntil } from './scheduling';
import submitRatingMixin, { SUBMIT_RATING_OUTCOME as OUTCOME } from './submitRatingMixin';

// How long the "ได้รับคะแนนแล้ว" confirmation stays up before handing off to
// PostRatingScreen - just long enough for the customer to register that the
// tap registered, not a second screen to sit through.
const CONFIRMATION_DISPLAY_MS = 1500;

export default {
  name: 'RatingScreen',
  components: { BranchLogo, RatingChoices },
  mixins: [submitRatingMixin],
  props: {
    branch: { type: Object, default: null },
    // Guaranteed status === 'waiting' with a valid, still-future expiresAt by
    // protocol.js's isRatableRatingSession() gate before this view is ever
    // reached - see sanitizeView()'s RATING branch.
    ratingSession: { type: Object, required: true }
  },
  data() {
    return {
      selected: null,
      confirmed: false,
      errorMessage: ''
    };
  },
  created() {
    this.customerDisplayCancelExpiryWatch = null;
    this.customerDisplayCancelConfirmationTimer = null;
    // Set once in beforeDestroy and checked after every await. The parent
    // keys this component by session id, so a new session destroys the old
    // instance; this flag prevents that old async continuation from emitting
    // after destruction.
    this.customerDisplayDestroyed = false;
  },
  mounted() {
    // Stop offering rating once expiresAt is reached - never extend the
    // window or manufacture a new one client-side (Phase 4 requirement #8).
    this.customerDisplayCancelExpiryWatch = watchUntil(this.ratingSession.expiresAt, () => this.$emit('expired'));
  },
  beforeDestroy() {
    this.customerDisplayDestroyed = true;
    if (this.customerDisplayCancelExpiryWatch) this.customerDisplayCancelExpiryWatch();
    if (this.customerDisplayCancelConfirmationTimer) this.customerDisplayCancelConfirmationTimer();
  },
  methods: {
    // Returns the submit result (so a Phase 6 hardware-remote intent can
    // report it back over the helper bridge), or null when this tap was a
    // no-op (already submitting, already selected, or the result arrived
    // for a session/instance that is no longer current -- the caller then
    // reports 'unavailable' rather than fabricating an outcome). Reused
    // as-is by the physical remote via submitFromHelper below: no rating/
    // session logic is duplicated for that path.
    async onSelect(rating) {
      if (this.ratingSubmitting || this.selected) return null; // one effective rating only
      // Snapshot before the await. The keyed instance can be destroyed while
      // the submit is in flight, and the session check also keeps this method
      // correct if the component is ever reused outside the keyed parent.
      const sessionId = this.ratingSession.id;
      this.selected = rating;
      this.errorMessage = '';
      const result = await this.submitRatingViaMixin({
        sessionId,
        ratingToken: this.ratingSession.ratingToken,
        rating
      });
      if (this.customerDisplayDestroyed || this.ratingSession.id !== sessionId) {
        // This instance moved on (or was torn down) while the request was
        // in flight -- the result belongs to nobody currently on screen and
        // must never touch confirmed/error state or emit anything.
        return null;
      }
      if (result.outcome === OUTCOME.SUCCESS || result.outcome === OUTCOME.ALREADY_RATED) {
        if (this.customerDisplayCancelExpiryWatch) this.customerDisplayCancelExpiryWatch();
        this.confirmed = true;
        // Second arg: the rating THIS submission actually attempted (never
        // the backend-reconciled one in result.session.rating, which for
        // ALREADY_RATED can legitimately differ -- a race lost to a
        // different input). The parent uses it to decide whether an RP2040
        // ACK would be truthful for this specific press (see
        // CustomerDisplayPage.vue's sendAckForOwnSubmission).
        this.customerDisplayCancelConfirmationTimer = scheduleAfter(CONFIRMATION_DISPLAY_MS, () => this.$emit('submitted', result.session, rating));
        return result;
      }
      if (result.outcome === OUTCOME.EXPIRED || result.outcome === OUTCOME.INVALID) {
        this.$emit('expired');
        return result;
      }
      // NETWORK_ERROR: re-enable controls, short customer-safe retry message.
      // The emit is purely observational (RP2040 RETRY cue, see
      // CustomerDisplayPage.vue's onNetworkError) -- it changes nothing
      // about this guard/retry/outcome handling above.
      this.selected = null;
      this.errorMessage = 'ไม่สามารถบันทึกคะแนนได้ กรุณาลองอีกครั้ง';
      this.$emit('network-error');
      return result;
    },
    // Vue 2 Options API exposes every method on the instance already (no
    // defineExpose needed) - this is the exact equivalent of the Vue 3
    // version's defineExpose({ submitFromHelper: onSelect }), reachable by
    // the parent via $refs.
    submitFromHelper(rating) {
      return this.onSelect(rating);
    }
  }
};
</script>

<style scoped>
.rating-screen {
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3.5vh;
  background: #f5f3ff;
  padding: 4vh 4vw;
}
.brand ::v-deep .logo {
  max-height: 16vh;
  max-width: 50vw;
}
.title {
  margin: 0;
  font-size: calc(3rem * var(--font-scale));
  font-weight: 600;
  color: #4c1d95;
  text-align: center;
}
.retry-message {
  margin: 0;
  font-size: calc(1.1rem * var(--font-scale));
  color: #b91c1c;
  text-align: center;
}
</style>
